import React from "react";
import { useLang } from "../lib/i18n";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Plus, Trash2, MapPin } from "lucide-react";

const EMPTY = { name: "", alerts_received: "", alerts_expected: "", contacts_total: "", contacts_seen: "", observation: "" };
const pct = (n, d) => (d && Number(d) > 0 ? `${Math.round((Number(n || 0) / Number(d)) * 100)} %` : "—");

export function AreasTable({ areas, onChange }) {
  const { t } = useLang();
  const set = (i, k, v) => onChange(areas.map((a, j) => (j === i ? { ...a, [k]: v } : a)));
  const cols = ["alerts_received", "alerts_expected", "contacts_seen", "contacts_total"];
  const labels = { alerts_received: "Alertes notifiées", alerts_expected: "Alertes attendues", contacts_seen: "Contacts vus", contacts_total: "Contacts à suivre" };

  return (
    <section className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm p-4 sm:p-5" data-testid="entry-areas">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-2"><MapPin className="h-4 w-4 text-[#F05A28]" />{t("Aires de santé d'appui")}</h2>
          <p className="text-xs text-slate-500 mt-0.5">{t("Une ligne par aire de santé suivie (hotspots). Les taux se calculent automatiquement.")}</p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={() => onChange([...areas, { ...EMPTY }])} data-testid="areas-add-btn">
          <Plus className="h-4 w-4 mr-1" />{t("Ajouter une aire")}
        </Button>
      </div>
      {areas.length === 0 && <p className="text-xs text-slate-400 italic" data-testid="areas-empty">{t("Aucune aire de santé ajoutée.")}</p>}
      <div className="space-y-3">
        {areas.map((a, i) => (
          <div key={i} className="rounded-md border border-slate-200 p-3" data-testid={`area-row-${i}`}>
            <div className="grid grid-cols-2 lg:grid-cols-7 gap-2 items-end">
              <div className="col-span-2">
                <label className="text-[11px] text-slate-500">{t("Aire de santé")}</label>
                <Input value={a.name} onChange={(e) => set(i, "name", e.target.value)} placeholder="ex. Nyakunde" className="h-10" data-testid={`area-name-${i}`} />
              </div>
              {cols.map((k) => (
                <div key={k}>
                  <label className="text-[11px] text-slate-500">{t(labels[k])}</label>
                  <Input type="number" min="0" inputMode="numeric" value={a[k] ?? ""} onChange={(e) => set(i, k, e.target.value)} className="h-10" data-testid={`area-${k}-${i}`} />
                </div>
              ))}
              <div className="flex items-center gap-2 justify-between">
                <div className="text-[11px] leading-tight text-slate-600" data-testid={`area-rates-${i}`}>
                  <div>{t("Alertes")} <b>{pct(a.alerts_received, a.alerts_expected)}</b></div>
                  <div>{t("Contacts")} <b>{pct(a.contacts_seen, a.contacts_total)}</b></div>
                </div>
                <button type="button" onClick={() => onChange(areas.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600" data-testid={`area-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
            <Input value={a.observation || ""} onChange={(e) => set(i, "observation", e.target.value)} placeholder={t("Observation (ex. bonne notification par rapport à hier)")} className="h-10 mt-2" data-testid={`area-obs-${i}`} />
          </div>
        ))}
      </div>
    </section>
  );
}

export function AreasReport({ areas }) {
  const { t } = useLang();
  if (!areas?.length) return null;
  return (
    <section className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm p-4 sm:p-5 mb-4" data-testid="gv-areas">
      <h2 className="text-sm font-semibold text-slate-900 mb-3">{t("Aires de santé d'appui")}</h2>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b">
            <th className="py-2 pr-3">{t("Aire de santé")}</th><th className="py-2 pr-3">{t("Alertes")}</th><th className="py-2 pr-3">{t("Contacts")}</th><th className="py-2">{t("Observation")}</th>
          </tr></thead>
          <tbody>
            {areas.map((a) => (
              <tr key={a.name} className="border-b border-slate-100" data-testid={`gv-area-${a.name}`}>
                <td className="py-2 pr-3 font-medium text-slate-800">{a.name}</td>
                <td className="py-2 pr-3">{a.alerts_received}/{a.alerts_expected || "—"} <span className="text-slate-500">({a.alert_rate ?? "—"}{a.alert_rate != null && " %"})</span></td>
                <td className="py-2 pr-3">{a.contacts_seen}/{a.contacts_total || "—"} <span className="text-slate-500">({a.contact_rate ?? "—"}{a.contact_rate != null && " %"})</span></td>
                <td className="py-2 text-slate-600 text-xs">{a.observation}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
